import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

import { Button, EmptyState, ErrorState, Money, Notice, PageHeader, Pagination, SkeletonRows, Status } from "../../../../components/ui";
import { focusRing } from "../../../../components/ui/cn";
import { businessApi } from "../../../../services/api";
import ConfirmReceiptDialog from "./ConfirmReceiptDialog";
import { MY_HR_PATH, PAGE_SIZE, confirmedMessage, payDate, paymentOf, payslipPath, periodLabel } from "./payslipView";

// My HR › My Payslips (UI-3B): the signed-in employee's own released payslips, newest first. The server resolves
// the employee from the session; no member id is sent. A payslip is not a payment — each row says where the salary
// stands, and a salary Finance has handed over waits here for the employee to confirm receipt.

const panel = "rounded-panel border border-default bg-surface";

function PayslipRow({ row }) {
  const payment = paymentOf(row);
  return (
    <li>
      <Link
        to={payslipPath(row.id)}
        className={`flex min-h-touch items-center gap-3 px-4 py-3 hover:bg-hover ${focusRing}`}
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate text-panel-title text-foreground">{periodLabel(row)}</span>
          {payDate(row) ? <span className="block text-meta text-secondary">Pay date {payDate(row)}</span> : null}
        </span>
        <span className="shrink-0 text-right">
          <span className="sr-only">Net pay </span>
          <Money value={row.net_pay_cents} className="block text-money text-foreground" />
          <Status tone={payment.tone} className="text-meta">{payment.label}</Status>
        </span>
        <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-tertiary" />
      </Link>
    </li>
  );
}

function ToConfirm({ row, onConfirm }) {
  const payment = paymentOf(row);
  return (
    <li className={`${panel} p-4`}>
      <div className="flex items-baseline justify-between gap-4">
        <p className="min-w-0 text-panel-title text-foreground">{periodLabel(row)}</p>
        <Money value={payment.amountCents} className="shrink-0 text-money text-foreground" />
      </div>
      <p className="mt-1 max-w-[75ch] text-body-sm text-secondary">{payment.detail}</p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Button variant="primary" onClick={() => onConfirm(row)}>Confirm salary received</Button>
        <Button as={Link} to={payslipPath(row.id)} variant="quiet">Open payslip</Button>
      </div>
    </li>
  );
}

export default function MyPayslips() {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ status: "loading", rows: [], total: 0 });
  const [confirming, setConfirming] = useState(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setState((current) => ({ ...current, status: current.rows.length ? "ready" : "loading" }));
    try {
      const result = await businessApi.getMyPayslips({ page, limit: PAGE_SIZE });
      setState({ status: "ready", rows: Array.isArray(result?.rows) ? result.rows : [], total: Number(result?.total || 0) });
    } catch {
      setState({ status: "error", rows: [], total: 0 });
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const { status, rows, total } = state;
  const toConfirm = rows.filter((row) => paymentOf(row).key === "confirm");
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const onConfirmed = (outcome) => {
    setConfirming(null);
    setNotice(confirmedMessage(outcome));
    load();
  };

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      <div>
        <Button as={Link} to={MY_HR_PATH} variant="quiet" size="sm" icon={ArrowLeft} className="-ml-3 mb-2">My HR</Button>
        <PageHeader
          title="My Payslips"
          description="Each payslip shows what you earned and what was deducted for one pay period. Your salary counts as paid once you confirm you received it."
        />
      </div>

      {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}

      {status === "loading" ? (
        <div className={panel}><SkeletonRows rows={4} columns={2} label="Loading your payslips" /></div>
      ) : status === "error" ? (
        <div className={panel}>
          <ErrorState title="Your payslips couldn't be loaded" description="Check your connection and try again. Nothing has changed." onRetry={load} />
        </div>
      ) : rows.length === 0 ? (
        <div className={panel}>
          <EmptyState title="No payslips yet" description="Payslips appear here after HR releases them." />
        </div>
      ) : (
        <>
          {toConfirm.length ? (
            <section aria-labelledby="payslips-to-confirm">
              <h2 id="payslips-to-confirm" className="text-section-title text-foreground">Waiting for you</h2>
              <ul className="mt-3 space-y-3">
                {toConfirm.map((row) => <ToConfirm key={row.id} row={row} onConfirm={setConfirming} />)}
              </ul>
            </section>
          ) : null}

          <section aria-labelledby="payslips-all">
            <h2 id="payslips-all" className={toConfirm.length ? "text-section-title text-foreground" : "sr-only"}>All payslips</h2>
            <div className={`${panel} ${toConfirm.length ? "mt-3" : ""} overflow-hidden`}>
              <div aria-hidden="true" className="flex justify-between gap-3 border-b border-subtle bg-surface-sunken px-4 py-2 pr-11 text-cell-head text-secondary">
                <span>Pay period</span>
                <span>Net pay</span>
              </div>
              <ul className="divide-y divide-subtle">
                {rows.map((row) => <PayslipRow key={row.id} row={row} />)}
              </ul>
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label="Payslip pages" />
          </section>
        </>
      )}

      {confirming ? (
        <ConfirmReceiptDialog
          payslip={confirming}
          onClose={() => { setConfirming(null); load(); }}
          onConfirmed={onConfirmed}
        />
      ) : null}
    </div>
  );
}
